import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, type EphemeralEnv, type EphemeralConfig } from '../api';
import { Page, Card, Button, Field, Input, Textarea, Badge, EmptyState } from '../ui';

const STATUS_VARIANT: Record<string, 'ok' | 'warn' | 'neutral' | 'accent'> = {
  running: 'ok',
  creating: 'accent',
  stopping: 'warn',
  error: 'warn',
  destroyed: 'neutral',
};

export function Ephemeral() {
  const { slug } = useParams<{ slug: string }>();
  const [config, setConfig] = useState<EphemeralConfig | null>(null);
  const [envs, setEnvs] = useState<EphemeralEnv[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);

  const [branch, setBranch] = useState('');
  const [ttl, setTtl] = useState(120);
  const [busy, setBusy] = useState(false);

  function reload() {
    if (!slug) return;
    api.listEphemeral(slug).then(({ environments }) => setEnvs(environments)).catch(() => undefined);
  }

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    Promise.all([api.getEphemeralConfig(slug), api.listEphemeral(slug)])
      .then(([c, e]) => {
        setConfig(c.config);
        setEnvs(e.environments);
        if (!c.config?.enabled) setShowConfig(true);
      })
      .catch(() => setErr('Falha ao carregar'))
      .finally(() => setLoading(false));
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function create() {
    if (!slug || !branch.trim() || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api.createEphemeral(slug, { branch: branch.trim(), mode: 'manual', ttlMinutes: ttl });
      setBranch('');
      reload();
    } catch (e) {
      const c = (e as { error?: string }).error;
      setErr(c === 'ephemeral_not_configured' ? 'Configure os efêmeros primeiro.' : c === 'no_free_port' ? 'Sem porta livre.' : 'Falha ao criar.');
    } finally {
      setBusy(false);
    }
  }

  async function destroy(id: string) {
    if (!slug) return;
    if (!confirm('Derrubar este ambiente?')) return;
    await api.destroyEphemeral(slug, id).catch(() => undefined);
    reload();
  }

  return (
    <Page
      title="Ambientes efêmeros"
      subtitle="Sobe uma cópia isolada do app por branch (compose + porta própria) para testar, e derruba."
      actions={
        <Button variant="ghost" onClick={() => setShowConfig((s) => !s)}>
          {showConfig ? 'ocultar config' : 'configurar'}
        </Button>
      }
    >
      {err && <div className="chat-error">{err}</div>}

      {showConfig && <ConfigForm slug={slug!} config={config} onSaved={(c) => setConfig(c)} />}

      {config?.enabled && (
        <Card style={{ marginBottom: 16 }}>
          <div className="card-title-row"><strong>Subir ambiente</strong></div>
          <div className="eph-create-row">
            <Field label="Branch">
              <Input value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="ex: bugpilot/report-abc123" />
            </Field>
            <Field label="TTL (min)">
              <Input type="number" value={ttl} onChange={(e) => setTtl(Number(e.target.value) || 120)} style={{ width: 90 }} />
            </Field>
            <Button variant="primary" onClick={create} disabled={busy || !branch.trim()}>
              {busy ? 'subindo…' : 'Subir'}
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="center">Carregando…</div>
      ) : envs.length === 0 ? (
        <EmptyState title="Nenhum ambiente" description="Suba um ambiente para uma branch acima (requer config habilitada)." />
      ) : (
        <div className="eph-list">
          {envs.map((e) => (
            <Card key={e.id} className="eph-item">
              <div className="eph-item-head">
                <Badge variant={STATUS_VARIANT[e.status] ?? 'neutral'}>{e.status}</Badge>
                <strong>{e.branch}</strong>
                <span className="users-muted">{e.composeProject} · porta {e.hostPort ?? '—'}</span>
              </div>
              {e.url && e.status === 'running' && (
                <a className="eph-url" href={e.url} target="_blank" rel="noreferrer">{e.url} ↗</a>
              )}
              {e.lastError && (
                <details className="eph-log" open={e.status === 'error'}>
                  <summary className="users-muted">saída do provisionamento</summary>
                  <div className="eph-error">{e.lastError}</div>
                </details>
              )}
              <div className="eph-item-foot">
                <span className="users-muted">
                  {e.ttlAt ? `expira ${new Date(e.ttlAt).toLocaleString()}` : 'sem TTL'}
                </span>
                {e.status !== 'destroyed' && e.status !== 'stopping' && (
                  <Button size="sm" variant="ghost" onClick={() => destroy(e.id)}>derrubar</Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}

function ConfigForm({ slug, config, onSaved }: { slug: string; config: EphemeralConfig | null; onSaved: (c: EphemeralConfig) => void }) {
  const [form, setForm] = useState({
    enabled: config?.enabled ?? false,
    composeFile: config?.composeFile ?? 'docker-compose.yml',
    repoUrl: config?.repoUrl ?? '',
    baseDomain: config?.baseDomain ?? 'dev.zighue.com.br',
    appPort: config?.appPort ?? 80,
    portBase: config?.portBase ?? 4300,
    migrateCmd: config?.migrateCmd ?? '',
    seedCmd: config?.seedCmd ?? '',
    sshHost: config?.sshHost ?? 'host.docker.internal',
    sshUser: config?.sshUser ?? 'bpinspect',
    sshKeyPath: config?.sshKeyPath ?? '/keys/inspect_ed25519',
    workdir: config?.workdir ?? '/opt/bug-pilot-data/ephemeral',
    defaultTtlMinutes: config?.defaultTtlMinutes ?? 120,
    // Modo subdomínio HTTPS (Traefik)
    composeTemplate: config?.composeTemplate ?? '',
    proxyService: config?.proxyService ?? '',
    proxyPort: config?.proxyPort ?? 80,
    proxyNetwork: config?.proxyNetwork ?? 'bugpilot-eph',
    traefikDynamicDir: config?.traefikDynamicDir ?? '/opt/bug-pilot/traefik/dynamic',
    preUpCmd: config?.preUpCmd ?? '',
    dbService: config?.dbService ?? '',
    dbCopyCmd: config?.dbCopyCmd ?? '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof form, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const { config } = await api.saveEphemeralConfig(slug, {
        ...form,
        repoUrl: form.repoUrl || null,
        migrateCmd: form.migrateCmd || null,
        seedCmd: form.seedCmd || null,
        composeTemplate: form.composeTemplate || null,
        proxyService: form.proxyService || null,
        preUpCmd: form.preUpCmd || null,
        dbService: form.dbService || null,
        dbCopyCmd: form.dbCopyCmd || null,
      });
      onSaved(config);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card style={{ marginBottom: 16 }}>
      <div className="card-title-row"><strong>Configuração</strong> <span className="users-muted">como subir a stack do projeto-alvo</span></div>
      <div className="eph-config-grid">
        <Field>
          <label className="agent-editor-toggle">
            <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} /> habilitado
          </label>
        </Field>
        <Field label="Compose file (no repo)"><Input value={form.composeFile} onChange={(e) => set('composeFile', e.target.value)} /></Field>
        <Field label="Repo URL (vazio = repo do projeto)"><Input value={form.repoUrl} onChange={(e) => set('repoUrl', e.target.value)} placeholder="(usa repoUrl do projeto)" /></Field>
        <Field label="Domínio base"><Input value={form.baseDomain} onChange={(e) => set('baseDomain', e.target.value)} /></Field>
        <Field label="Porta do app (no container)"><Input type="number" value={form.appPort} onChange={(e) => set('appPort', Number(e.target.value) || 80)} /></Field>
        <Field label="Porta base (host)"><Input type="number" value={form.portBase} onChange={(e) => set('portBase', Number(e.target.value) || 4300)} /></Field>
        <Field label="migrate cmd (opcional)"><Input value={form.migrateCmd} onChange={(e) => set('migrateCmd', e.target.value)} placeholder="ex: docker compose -p $COMPOSE_PROJECT_NAME exec -T backend ./mvnw flyway:migrate" /></Field>
        <Field label="seed cmd (opcional)"><Input value={form.seedCmd} onChange={(e) => set('seedCmd', e.target.value)} /></Field>
        <Field label="SSH host"><Input value={form.sshHost} onChange={(e) => set('sshHost', e.target.value)} /></Field>
        <Field label="SSH user"><Input value={form.sshUser} onChange={(e) => set('sshUser', e.target.value)} /></Field>
        <Field label="SSH key path (no container)"><Input value={form.sshKeyPath} onChange={(e) => set('sshKeyPath', e.target.value)} /></Field>
        <Field label="Workdir (host)"><Input value={form.workdir} onChange={(e) => set('workdir', e.target.value)} /></Field>
        <Field label="TTL default (min)"><Input type="number" value={form.defaultTtlMinutes} onChange={(e) => set('defaultTtlMinutes', Number(e.target.value) || 120)} /></Field>
      </div>
      <div className="card-title-row" style={{ marginTop: 18 }}>
        <strong>Modo subdomínio HTTPS (Traefik)</strong>
        <span className="users-muted">preenchido = liga; cada efêmero vira https://&lt;id&gt;.&lt;domínio&gt; com cópia da base</span>
      </div>
      <Field label="Template do docker-compose" hint="Renderizado por efêmero. Placeholders: ${EPH_PROJECT} ${EPH_DB} ${EPH_NET} ${EPH_HOST} ${BRANCH} ${EPH_DIR}. Os serviços web/db entram na rede ${EPH_NET}; sem portas de host.">
        <Textarea
          rows={14}
          spellCheck={false}
          value={form.composeTemplate}
          onChange={(e) => set('composeTemplate', e.target.value)}
          placeholder={'services:\n  frontend:\n    image: ghcr.io/org/app/frontend:dev\n    networks: [${EPH_NET}]\n  backend:\n    image: ghcr.io/org/app/backend:dev\n    environment:\n      DB_HOST: postgres\n      DB_NAME: ${EPH_DB}\n    networks: [${EPH_NET}, default]\n  postgres:\n    image: postgres:16-alpine\n    environment:\n      POSTGRES_DB: ${EPH_DB}\n    networks: [default]\nnetworks:\n  ${EPH_NET}:\n    external: true'}
        />
      </Field>
      <div className="eph-config-grid">
        <Field label="Serviço web (subdomínio)" hint="serviço do template que recebe o tráfego"><Input value={form.proxyService} onChange={(e) => set('proxyService', e.target.value)} placeholder="frontend" /></Field>
        <Field label="Porta do serviço web"><Input type="number" value={form.proxyPort} onChange={(e) => set('proxyPort', Number(e.target.value) || 80)} /></Field>
        <Field label="Rede do Traefik"><Input value={form.proxyNetwork} onChange={(e) => set('proxyNetwork', e.target.value)} /></Field>
        <Field label="Dir dinâmico do Traefik (host)"><Input value={form.traefikDynamicDir} onChange={(e) => set('traefikDynamicDir', e.target.value)} /></Field>
        <Field label="Serviço de DB (cópia)" hint="subido isolado e populado antes do resto"><Input value={form.dbService} onChange={(e) => set('dbService', e.target.value)} placeholder="postgres" /></Field>
      </div>
      <Field label="Pré-up: gerar .env em runtime (opcional)" hint="Roda no workdir antes do up. Use docker inspect → arquivos .env para não persistir segredos. Recebe ${EPH_*}.">
        <Textarea
          rows={3}
          spellCheck={false}
          value={form.preUpCmd}
          onChange={(e) => set('preUpCmd', e.target.value)}
          placeholder={"docker inspect -f '{{range .Config.Env}}{{println .}}{{end}}' app-backend-1 > backend.env"}
        />
      </Field>
      <Field label="Comando de cópia da base develop (opcional)" hint="Roda no host após o DB ficar pronto. Recebe ${EPH_PROJECT} ${EPH_DB} etc.">
        <Textarea
          rows={3}
          spellCheck={false}
          value={form.dbCopyCmd}
          onChange={(e) => set('dbCopyCmd', e.target.value)}
          placeholder={'docker exec <pg-develop> pg_dump -U user -d devdb | docker exec -i ${EPH_PROJECT}-postgres-1 psql -U user -d ${EPH_DB}'}
        />
      </Field>
      <div className="eph-config-note">
        <strong>Modo legado (sem template):</strong> o compose do repo deve mapear a porta via <code>${'{APP_HOST_PORT}'}</code>;
        exposição por porta (<code>http://domínio:porta</code>). <strong>Modo Traefik (template preenchido):</strong> subdomínio
        HTTPS automático em <code>https://&lt;id&gt;.{form.baseDomain}</code> — requer o edge (nginx wildcard + Traefik) já configurado no host.
      </div>
      <Button variant="primary" onClick={save} disabled={saving}>{saving ? 'salvando…' : 'salvar config'}</Button>
    </Card>
  );
}
